import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Skeleton,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
} from "@mui/material";
import {
  LocalFireDepartment as FireIcon,
  TrendingUp as TrendingUpIcon,
  NewReleases as NewIcon,
  Person as PersonIcon,
  EmojiEvents as TrophyIcon,
} from "@mui/icons-material";
import { useTheme } from "./ThemeContext";

const API_BASE_URL = process.env.REACT_APP_SC_API_BASE_URL || "http://localhost:8080";

function CommunityLeftSidebar({ communityName, communityId }) {
  const { theme } = useTheme();
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("hot");

  useEffect(() => {
    const fetchCommunityTrending = async () => {
      if (!communityId) return;

      try {
        setLoading(true);

        const postsEndpoint = sortBy === "hot"
          ? `${API_BASE_URL}/api/trending/community/${communityId}/posts/hot?page=0&size=5`
          : sortBy === "trending"
          ? `${API_BASE_URL}/api/trending/community/${communityId}/posts?page=0&size=5`
          : `${API_BASE_URL}/api/trending/community/${communityId}/posts/new?page=0&size=5`;

        const [postsRes, usersRes] = await Promise.all([
          fetch(postsEndpoint),
          fetch(`${API_BASE_URL}/api/trending/users?limit=5`),
        ]);

        if (postsRes.ok) {
          const postsData = await postsRes.json();
          setPosts(postsData);
        }
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setUsers(usersData);
        }
      } catch (err) {
        console.error("Error fetching community trending:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCommunityTrending();
  }, [communityId, sortBy]);

  const handleSortChange = (event, newSort) => {
    if (newSort !== null) {
      setSortBy(newSort);
    }
  };

  const getRankStyle = (index) => {
    if (index === 0) return { color: "#FFD700", fontWeight: 700 };
    if (index === 1) return { color: "#C0C0C0", fontWeight: 600 };
    if (index === 2) return { color: "#CD7F32", fontWeight: 600 };
    return { color: theme.palette.text.disabled, fontWeight: 500 };
  };

  const SectionHeader = ({ icon: Icon, title, gradient, showSort = false }) => (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          pb: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            background: gradient || `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        >
          <Icon sx={{ fontSize: 18, color: "#fff" }} />
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            fontSize: "0.9rem",
            letterSpacing: "0.3px",
            flex: 1,
          }}
        >
          {title}
        </Typography>
      </Box>
      {showSort && (
        <ToggleButtonGroup
          value={sortBy}
          exclusive
          onChange={handleSortChange}
          size="small"
          sx={{
            mt: 1.5,
            display: "flex",
            gap: 0.5,
            "& .MuiToggleButton-root": {
              flex: 1,
              px: 1.5,
              py: 0.5,
              fontSize: "0.7rem",
              textTransform: "none",
              color: alpha(theme.palette.text.secondary, 0.7),
              borderColor: alpha(theme.palette.divider || "#fff", 0.15),
              borderRadius: "8px !important",
              border: `1px solid ${alpha(theme.palette.divider || "#fff", 0.15)} !important`,
              "&.Mui-selected": {
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: "#fff",
                borderColor: "transparent !important",
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                },
              },
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              },
            },
          }}
        >
          <ToggleButton value="hot">
            <FireIcon sx={{ fontSize: 14, mr: 0.5 }} />
            Hot
          </ToggleButton>
          <ToggleButton value="trending">
            <TrendingUpIcon sx={{ fontSize: 14, mr: 0.5 }} />
            Rising
          </ToggleButton>
          <ToggleButton value="new">
            <NewIcon sx={{ fontSize: 14, mr: 0.5 }} />
            New
          </ToggleButton>
        </ToggleButtonGroup>
      )}
    </Box>
  );

  const PostItem = ({ post, index }) => (
    <Link to={`/post/${post.postId}`} style={{ textDecoration: "none" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          py: 1.5,
          px: 1.5,
          borderRadius: "8px",
          transition: "all 0.2s ease",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "rgba(98,244,129,0.06)",
            transform: "translateX(4px)",
          },
        }}
      >
        <Typography
          sx={{
            ...getRankStyle(index),
            fontSize: "0.9rem",
            minWidth: 24,
            textAlign: "center",
          }}
        >
          {index + 1}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: "#f9f9cf",
              fontSize: "0.85rem",
              fontWeight: 500,
              lineHeight: 1.4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              mb: 0.5,
            }}
          >
            {post.title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: "6px",
                backgroundColor: "rgba(98,244,129,0.1)",
              }}
            >
              <TrendingUpIcon sx={{ fontSize: 12, color: "#62f481" }} />
              <Typography sx={{ color: "#62f481", fontSize: "0.7rem", fontWeight: 600 }}>
                {(post.upvotes || 0) - (post.downvotes || 0)}
              </Typography>
            </Box>
            <Typography sx={{ color: "#6a7282", fontSize: "0.7rem" }}>
              {post.commentCount || 0} comments
            </Typography>
          </Box>
        </Box>
      </Box>
    </Link>
  );

  const UserItem = ({ user, index }) => (
    <Link to={`/profile/${user.username}`} style={{ textDecoration: "none" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          py: 1.5,
          px: 1.5,
          borderRadius: "8px",
          transition: "all 0.2s ease",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "rgba(98,244,129,0.06)",
            transform: "translateX(4px)",
          },
        }}
      >
        <Typography
          sx={{
            ...getRankStyle(index),
            fontSize: "0.9rem",
            minWidth: 24,
            textAlign: "center",
          }}
        >
          {index + 1}
        </Typography>
        <Box sx={{ position: "relative" }}>
          <Avatar
            src={user.avatarUrl ? `${API_BASE_URL}/api/files/download/${user.avatarUrl}` : undefined}
            sx={{
              width: 36,
              height: 36,
              bgcolor: `hsl(${(user.username?.charCodeAt(0) * 20) % 360}, 50%, 50%)`,
              fontSize: "0.85rem",
              fontWeight: 600,
              border: `2px solid rgba(98,244,129,0.3)`,
            }}
          >
            {user.username?.charAt(0).toUpperCase()}
          </Avatar>
          {index === 0 && (
            <TrophyIcon
              sx={{
                position: "absolute",
                bottom: -4,
                right: -4,
                fontSize: 16,
                color: "#FFD700",
                filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
              }}
            />
          )}
        </Box>
        <Typography
          sx={{
            color: "#f9f9cf",
            fontSize: "0.85rem",
            fontWeight: 500,
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          u/{user.username}
        </Typography>
      </Box>
    </Link>
  );

  const LoadingSkeleton = ({ count = 3 }) => (
    <>
      {[...Array(count)].map((_, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5, px: 1.5 }}>
          <Skeleton variant="text" width={24} height={24} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="90%" height={20} />
            <Skeleton variant="text" width="60%" height={16} />
          </Box>
        </Box>
      ))}
    </>
  );

  const Section = ({ children }) => (
    <Box
      sx={{
        backgroundColor: "#1a1f26",
        borderRadius: "12px",
        p: 2,
        mb: 2,
        border: "1px solid #808080",
        boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
      }}
    >
      {children}
    </Box>
  );

  const EmptyState = ({ message }) => (
    <Box sx={{ py: 3, textAlign: "center" }}>
      <Typography
        sx={{
          color: "#6a7282",
          fontSize: "0.85rem",
          fontStyle: "italic",
        }}
      >
        {message}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 280,
        pt: 2,
      }}
    >
      {/* Community Posts Section */}
      <Section>
        <SectionHeader
          icon={FireIcon}
          title={communityName ? `${communityName}` : "Community Posts"}
          gradient="linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)"
          showSort
        />
        {loading ? (
          <LoadingSkeleton count={4} />
        ) : posts.length > 0 ? (
          posts.map((post, index) => (
            <PostItem key={post.postId} post={post} index={index} />
          ))
        ) : (
          <EmptyState message="No posts yet" />
        )}
      </Section>

      {/* Top Contributors Section */}
      <Section>
        <SectionHeader
          icon={PersonIcon}
          title="Top Contributors"
          gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
        />
        {loading ? (
          <LoadingSkeleton count={4} />
        ) : users.length > 0 ? (
          users.map((user, index) => (
            <UserItem key={user.id} user={user} index={index} />
          ))
        ) : (
          <EmptyState message="No contributors yet" />
        )}
      </Section>
    </Box>
  );
}

export default CommunityLeftSidebar;
